import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface User {
  title: string;
  description: string;
  useCases: string[];
}

export function TargetUsers() {
  const { t, tArray } = useTranslation();
  const users = tArray<User>('targetUsers.users');

  return (
    <section id="users" className="bg-slate-50 dark:bg-[#0a0f14] py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('targetUsers.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl tracking-tight">
            {t('targetUsers.title')}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {users.map((user, index) => (
            <motion.div
              key={user.title}
              className="group relative p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0d1318]/50 hover:border-slate-300 dark:hover:border-[#3d5a6e] transition-all duration-300 hover:shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {/* Corner accent */}
              {/* <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                <div className="absolute top-0 right-0 w-px h-8 bg-slate-300 dark:bg-[#3d5a6e] group-hover:bg-[#5a8a9a] transition-colors" />
                <div className="absolute top-0 right-0 h-px w-8 bg-slate-300 dark:bg-[#3d5a6e] group-hover:bg-[#5a8a9a] transition-colors" />
              </div> */}

              <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-xl md:text-2xl mb-4">
                {user.title}
              </h3>
              <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed mb-6">
                {user.description}
              </p>

              <div className="pt-6 border-t border-slate-200 dark:border-[#1a2530]">
                <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-xs tracking-wider uppercase mb-3 block font-medium">
                  {t('targetUsers.keyUseCases')}
                </span>
                <ul className="space-y-2">
                  {user.useCases.map((useCase, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-slate-700 dark:text-[#c8d0d2] text-sm md:text-base"
                    >
                      <div className="w-1 h-1 bg-[#5a8a9a]" />
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
